import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createBootstrap } from 'bootstrap-vue-next';
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css';
import '@/style/app.scss';

import App from '@/App.vue';
import i18n from '@/i18n';
import { createAppRouter } from '@/router';
import { authService } from '@/auth/authService';
import { loadSpaConfig } from '@/config/spaConfig';
import { useCustomerStore } from '@/stores/customer';
import { usePreferencesStore } from '@/stores/preferences';
import { initTelemetry, trackException, trackRouter } from '@/telemetry/appInsights';
import { initAnalytics, trackAnalyticsRouter } from '@/telemetry/analytics';
import { readConsentCookie } from '@/composables/useCookieConsent';

/**
 * Start-up order matters:
 *  1. runtime config from the ASP.NET host
 *  2. MSAL: consume the B2C redirect + restore the session  (before any routing!)
 *  3. customer profile, if logged in
 *  4. install the router (first navigation) and mount
 */
async function bootstrap(): Promise<void> {
  const config = await loadSpaConfig();

  const app = createApp(App);
  const pinia = createPinia();
  app.use(pinia);
  app.use(i18n);
  app.use(createBootstrap());

  const preferences = usePreferencesStore();
  // The consent cookie (set by the banner) is the source of truth; no cookie = not answered = no consent.
  const consent = readConsentCookie() === true;
  if (preferences.analyticsConsent !== consent) preferences.analyticsConsent = consent;
  initTelemetry(config.appInsightConnectionString, consent);
  initAnalytics(config.gaCode, consent);
  app.config.errorHandler = (error, _instance, info) => {
    console.error(error);
    trackException(error, { info });
  };

  const { returnTo } = await authService.initialize(config.msalConfig);

  if (authService.isAuthenticated) {
    try {
      await useCustomerStore().load();
    } catch (error) {
      // The session is valid but the profile could not be loaded: views handle a
      // missing profile; do not block the whole app on it.
      console.warn('Could not load customer profile', error);
    }
  }

  // Coming back from B2C: resume where the user started (QR page, recharge, ...).
  // Done before the router is installed so its first navigation is the right one.
  if (returnTo) {
    window.history.replaceState(window.history.state, '', returnTo);
  }

  const router = createAppRouter();
  trackRouter(router);
  trackAnalyticsRouter(router);
  app.use(router);
  await router.isReady();
  app.mount('#app');
}

bootstrap().catch((error) => {
  console.error('CogesKey portal failed to start', error);
  trackException(error);
  const root = document.getElementById('app');
  if (root) {
    root.innerHTML =
      '<div style="padding:2rem;font-family:sans-serif;text-align:center">' +
      '<p>Service temporarily unavailable. Please try again later.</p>' +
      '<button onclick="sessionStorage.removeItem(\'spa.config\');location.reload()">Retry</button></div>';
  }
});
